import CloseIcon from '@mui/icons-material/Close'
import TextSnippetIcon from '@mui/icons-material/TextSnippet'
import {
  Box,
  Checkbox,
  Divider,
  IconButton,
  InputLabel,
  TextField,
  TextareaAutosize,
  Typography,
  styled
} from '@mui/material'

import { MButton, SvgImage } from 'components'
import { CAMPAIGN_ACCORDION_ITEMS } from 'config'
import { color } from 'config/color'
import { isEmpty } from 'lodash'
import { useState } from 'react'
import { type Control, Controller } from 'react-hook-form'
import { UploadCsvDialogBox } from './UploadCsvDialogBox'

interface ICampaignAccordions {
  setValue?: any
  control?: Control<any>
  watch: any
}

export const CampaignAccordions = ({
  setValue,
  control,
  watch
}: ICampaignAccordions): JSX.Element => {
  const [showDialog, setShowDialog] = useState(false)
  const [templateType, setTemplateType] = useState('')

  return (
    <>
      {CAMPAIGN_ACCORDION_ITEMS.map((item) => (
        <Box key={item.templateType}>
          <Box
            display="flex"
            mt={3}
            bgcolor={color.secondary.dark[50]}
            borderTop={`1px solid ${color.secondary.dark[300]}`}
            borderLeft={`1px solid ${color.secondary.dark[300]}`}
            borderRight={`1px solid ${color.secondary.dark[300]}`}
            borderBottom={
              !watch(`${item.templateType}.isActive`)
                ? `1px solid ${color.secondary.dark[300]}`
                : 'none'
            }
            p={1}
            justifyContent="space-between"
            flexWrap="wrap"
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                display="flex"
                bgcolor={color.white}
                borderRadius="20%"
                p={0.25}
                m={0.25}
                border={`1px solid ${color.secondary.dark[200]}`}
              >
                <item.icon
                  sx={{
                    color: watch(`${item.templateType}.isActive`)
                      ? color.primary[600]
                      : color.secondary.dark[500]
                  }}
                />
              </Box>

              <Typography
                color={color.grey[900]}
                fontWeight={400}
                fontSize={18}
              >
                {item.header}
              </Typography>
            </Box>

            <Controller
              name={`${item.templateType}.isActive`}
              control={control}
              render={({ field: { onChange, value, name } }): JSX.Element => (
                <Box display="flex" flexDirection="row" alignItems="center">
                  <Checkbox
                    name={name}
                    icon={
                      <SvgImage name="CheckboxOutline" width={14} height={14} />
                    }
                    checkedIcon={
                      <SvgImage
                        name="CheckboxIconGreen"
                        width={14}
                        height={14}
                      />
                    }
                    checked={value}
                    onChange={(e) => {
                      onChange(e.target.checked)
                    }}
                  />
                  <Typography
                    color={color.grey[800]}
                    fontWeight={400}
                    fontSize={12}
                  >
                    Active
                  </Typography>
                </Box>
              )}
            />
          </Box>

          {watch(`${item.templateType}.isActive`) && (
            <Box>
              {item.templateType === 'email'
                ? (
                  <Box
                    bgcolor={color.grey[50]}
                    borderLeft={`1px solid ${color.secondary.dark[300]}`}
                    borderRight={`1px solid ${color.secondary.dark[300]}`}
                    borderTop={`1px solid ${color.grey[50]}`}
                    px={2.5}
                    py={1.5}
                  >
                    <FormLabel>Email Subject</FormLabel>

                    <Controller
                      name={`${item.templateType}.subject`}
                      control={control}
                      rules={{
                        required: watch(`${item.templateType}.isActive`)
                      }}
                      render={({
                        field: { onChange, value, name }
                      }): JSX.Element => (
                        <Box bgcolor={color.white}>
                          <TextField
                            fullWidth
                            type="text"
                            name={name}
                            placeholder="Enter Email Subject"
                            onChange={onChange}
                            value={value}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  padding: 0,

                                  borderColor: color.secondary.dark[300]
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: color.primary[700]
                                }
                              }
                            }}
                          />
                        </Box>
                      )}
                    />

                    <FormLabel>HTML</FormLabel>

                    <Divider sx={{ color: color.secondary.dark[300] }} />

                    <Controller
                      name={`${item.templateType}.htmlTemplate`}
                      control={control}
                      rules={{
                        required: watch(`${item.templateType}.isActive`)
                      }}
                      render={({
                        field: { onChange, value, name }
                      }): JSX.Element => (
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="flex-start"
                        >
                          <TextArea
                            minRows={4}
                            placeholder="Enter HTML"
                            onChange={onChange}
                            value={value}
                            name={name}
                          />
                        </Box>
                      )}
                    />

                    <Divider sx={{ color: color.secondary.dark[300] }} />

                    <FormLabel>Plain Text</FormLabel>

                    <Divider sx={{ color: color.secondary.dark[300] }} />

                    <Controller
                      name={`${item.templateType}.plainTextTemplate`}
                      control={control}
                      rules={{
                        required: watch(`${item.templateType}.isActive`)
                      }}
                      render={({
                        field: { onChange, value, name }
                      }): JSX.Element => (
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="flex-start"
                        >
                          <TextArea
                            minRows={4}
                            placeholder="Enter Plain Text"
                            onChange={onChange}
                            value={value}
                            name={name}
                          />
                        </Box>
                      )}
                    />

                    <Divider sx={{ color: color.secondary.dark[300] }} />
                  </Box>
                )
                : (
                  <Controller
                    name={`${item.templateType}.template`}
                    control={control}
                    rules={{
                      required: watch(`${item.templateType}.isActive`)
                    }}
                    render={({
                      field: { onChange, value, name }
                    }): JSX.Element => (
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-start"
                      >
                        <TextArea
                          placeholder="Enter Message"
                          onChange={onChange}
                          value={value}
                          name={name}
                        />
                      </Box>
                    )}
                  />
                )}

              {isEmpty(watch(`${item.templateType}.file`)?.name)
                ? (
                  <Controller
                    name={`${item.templateType}.file`}
                    control={control}
                    render={({ field: { onChange } }): JSX.Element => (
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        border={`1px solid ${color.secondary.dark[300]}`}
                        p={1.5}
                      >
                        <MButton
                          component="label"
                          type="button"
                          size="medium"
                          rounded="true"
                          sx={{ fontSize: 16 }}
                          texttransform="none"
                          onClick={() => {
                            setTemplateType(item.templateType)
                            setShowDialog(true)
                          }}
                        >
                          <Typography
                            color={color.primary[700]}
                            fontWeight={400}
                            fontSize={16}
                          >
                            Upload CSV File
                          </Typography>
                        </MButton>
                        <UploadCsvDialogBox
                          isOpen={showDialog}
                          handleClose={() => {
                            setShowDialog(false)
                          }}
                          templateType={templateType}
                          onChange={onChange}
                        />
                      </Box>
                    )}
                  />
                )
                : (
                  <Box
                    display="flex"
                    p={1.5}
                    border={`1px solid ${color.secondary.dark[300]}`}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box display="flex" gap={1}>
                      <TextSnippetIcon
                        sx={{
                          color: color.secondary.dark[500]
                        }}
                      />
                      <Typography
                        color={color.black}
                        fontWeight={400}
                        fontSize={16}
                      >
                        {watch(`${item.templateType}.file`)?.name}
                      </Typography>
                    </Box>

                    <Box>
                      <IconButton
                        onClick={() => {
                          setValue(`${item.templateType}.file`, null)
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  </Box>
                )}
            </Box>
          )}
        </Box>
      ))}
    </>
  )
}

export const TextArea = styled(TextareaAutosize)({
  width: '100%',
  height: '188px !important',
  resize: 'none',
  font: 'inherit',
  padding: '10px !important',
  borderTop: `1px solid ${color.white} !important`,
  borderLeft: `1px solid ${color.secondary.dark[300]} !important`,
  borderRight: `1px solid ${color.secondary.dark[300]} !important`,
  borderBottom: `1px solid ${color.white} !important`,
  ':focus': {
    border: `1px solid ${color.primary[700]} !important`
  },
  ':hover:not(:focus)': {
    borderTop: `1px solid ${color.white} !important`,
    borderLeft: `1px solid ${color.secondary.dark[300]} !important`,
    borderRight: `1px solid ${color.secondary.dark[300]} !important`,
    borderBottom: `1px solid ${color.white} !important`
  },
  ':focus-visible': {
    outline: `1px solid ${color.white} !important`
  },
  '::placeholder': {
    color: color.grey[700]
  }
})

const FormLabel = styled(InputLabel)({
  fontSize: 12,
  color: color.grey[700],
  textTransform: 'uppercase',
  letterSpacing: 2,
  marginBottom: 10,
  marginTop: 10,
  whiteSpace: 'normal',
  wordWrap: 'break-word'
})
